import type { ComponentProps, ReactNode } from 'react';
import * as TogglePrimitive from '@radix-ui/react-toggle';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const toggleVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-secondary data-[state=on]:text-secondary-foreground hover:bg-secondary hover:text-secondary-foreground',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline:
          'border border-input bg-transparent shadow-xs hover:bg-secondary hover:text-secondary-foreground',
      },
      size: {
        default: 'h-9 px-4',
        sm: 'h-8 px-2',
        lg: 'h-10 px-6',
        icon: 'h-9 w-9',
        'icon-sm': 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'icon-sm',
    },
  },
);

export interface ToggleProps
  extends Omit<ComponentProps<typeof TogglePrimitive.Root>, 'children'>,
    VariantProps<typeof toggleVariants> {
  children?: ReactNode;
}

function Toggle({ className, variant, size, children, ...props }: ToggleProps) {
  return (
    <TogglePrimitive.Root className={cn(toggleVariants({ variant, size, className }))} {...props}>
      {children}
    </TogglePrimitive.Root>
  );
}

export { Toggle, toggleVariants };
